import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  Col,
  Input,
  Table,
  Spinner,
  CardFooter,
  Button,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { claimHeaders, EXPENSE_CODETYPES } from './constants';
import {
  fetchExpenseHistory,
  fetchCodeList,
} from '../../../store/apis/expenseService';
import { useTableHook } from '../../../components/hooks';
import { fetchTableColumnValues } from '../../../store/apis/commonService';
import {
  FilterDropdown,
  TableFilterBubbles,
  TableSortDisplayArrow,
  DatePicker,
  TablePagination,
} from '../../../components/Common';
import { AuthUtil } from '../../../utils';
import { formatAmountWithCommas } from '../../../utils/number.util';
import { Messages } from '../../../common/messages';

function ExpenseEnquiry() {
  document.title = 'Expense Claim Enquiry | Envision Daikin';
  const initialState = {
    queryType: 'ENQUIRY',
    claimStatus: 'Pending',
    claimFromDate: '',
    employeeId: AuthUtil.getAuthUser()?.employeeId ?? '',
    claimToDate: '',
    expenseGroup: '',
    expenseCategory: '',
    expenseType: '',
    sorNo: '',
    claimNo: '',
  };
  const navigate = useNavigate();
  // Table Hook
  const {
    data,
    pageRequest,
    setPageRequest,
    externalFilters,
    setExternalFilters,
    resetFilters,
    columnData,
    setColumnName,
    columnName,
    applyFilter,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    applyExternalFilters,
    isLoadingData,
    isLoadingColumn,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'ClaimNo',
      sortDirection: 'DESC',
      filters: [],
      queryType: 'ENQUIRY',
      claimStatus: 'Pending',
      employeeId: AuthUtil.getAuthUser()?.employeeId ?? '',
    },
    fetchFn: fetchExpenseHistory,
    fetchColumnFn: fetchTableColumnValues,
    columnKeyPrefix: 'expenseEnquiryColumn',
    queryKeyPrefix: 'expenseEnquiry',
    tableName: 'ExpenseEnquiry',
    defaultExternalFilters: {
      ...initialState,
    },
  });

  // React Use Query
  // Get Expense Group
  const { data: expenseGroupData } = useQuery({
    queryKey: ['expenseGroup'],
    queryFn: () =>
      fetchCodeList({
        codeType: EXPENSE_CODETYPES.EXPENSE_GRP,
      }),
    enabled: true,
  });

  // Get Expense Category
  const { data: expenseCategoryData } = useQuery({
    queryKey: ['expenseCategory'],
    queryFn: () =>
      fetchCodeList({
        codeType: EXPENSE_CODETYPES.EXPENSE_CATEGORY,
      }),
    enabled: true,
  });

  // Get Expense Type based on Expense Category
  const { data: expenseTypeData } = useQuery({
    queryKey: ['expenseType', externalFilters?.expenseCategory],
    queryFn: () =>
      fetchCodeList({
        codeType: EXPENSE_CODETYPES.EXPENSE_TYPE,
        param1: externalFilters?.expenseCategory,
      }),
    enabled: !!externalFilters?.expenseCategory,
  });

  const handleLinkToView = item => {
    navigate(`/expense/claim/detail?ID=${item?.ClaimNo}`);
  };

  const renderTableBody = () => {
    const tableData = data?.data || [];

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={7}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_EXPENSES
            )}
          </td>
        </tr>
      );
    }

    return tableData?.map(item => (
      <tr
        key={item.ClaimNo}
        onClick={() => handleLinkToView(item)}
        className="cursor-pointer"
      >
        <td className="cursor-pointer text-decoration-underline text-primary text-header">
          {item.ClaimNo}
        </td>
        <td>{item.ClaimDt || '-'}</td>
        <td>{item.Employee}</td>
        <td>{item.Curr}</td>
        <td>{formatAmountWithCommas(item.Amount)}</td>
        <td>{item.ClaimStatus}</td>
        <td>{item['Settlement Date'] ?? '-'}</td>
      </tr>
    ));
  };

  const externalFiltersValidation = () => {
    if (
      externalFilters.claimFromDate &&
      externalFilters.claimToDate &&
      new Date(externalFilters?.claimFromDate) >
        new Date(externalFilters?.claimToDate)
    ) {
      toast.error('To Date should be greater than equal to From Date.', {
        className: 'toast-error',
      });
      return false;
    }
    return true;
  };

  return (
    <div className="page-content main-table-page">
      <Card className="enquiry-card">
        <CardBody className="p-0">
          <div className="p-3">
            <span className="fw-semibold fs-5">Expense Claim Enquiry</span>
          </div>
          <div className="external-filter gap-2 mx-3 p-3">
            <Col lg={12}>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex flex-row gap-2">
                  <Col className="external-filter-col">
                    <b>Claim Number</b>
                    <div className="search-box">
                      <Input
                        type="text"
                        className="form-control external-filter-input"
                        placeholder="Search"
                        value={externalFilters?.claimNo}
                        onChange={e =>
                          setExternalFilters(prevFilters => ({
                            ...prevFilters,
                            claimNo: e.target.value,
                          }))
                        }
                        onKeyDown={e => {
                          if (e.key === 'Enter') applyExternalFilters();
                        }}
                      />
                      <span className="search-icon ri-search-line" />
                    </div>
                  </Col>
                  <Col className="external-filter-col">
                    <b>Claim Status</b>
                    <Input
                      type="select"
                      className="form-select external-filter-input"
                      value={externalFilters?.claimStatus}
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          claimStatus: e.target.value,
                        }))
                      }
                    >
                      <option value="Settled">Settled</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Pending">Pending</option>
                    </Input>
                  </Col>
                  <Col className="external-filter-col">
                    <b>Claim From Date</b>
                    <DatePicker
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          claimFromDate: e,
                        }))
                      }
                      className="form-control external-filter-input"
                      value={externalFilters?.claimFromDate || ''}
                    />
                  </Col>
                  <Col className="external-filter-col">
                    <b>Claim To Date</b>
                    <DatePicker
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          claimToDate: e,
                        }))
                      }
                      className="form-control external-filter-input"
                      value={externalFilters?.claimToDate || ''}
                    />
                  </Col>
                </div>
                <div className="d-flex flex-row gap-2 mb-1">
                  <Col className="external-filter-col">
                    <b>Expense Group</b>
                    <Input
                      type="select"
                      className="form-select external-filter-input"
                      value={externalFilters?.expenseGroup}
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          expenseGroup: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select Expense Group</option>
                      {expenseGroupData?.map(item => (
                        <option key={item.Code} value={item.Code}>
                          {item.Description}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col className="external-filter-col">
                    <b>Expense Category</b>
                    <Input
                      type="select"
                      className="form-select external-filter-input"
                      value={externalFilters?.expenseCategory}
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          expenseCategory: e.target.value,
                          expenseType: '',
                        }))
                      }
                    >
                      <option value="">Select Expense Category</option>
                      {expenseCategoryData?.map(item => (
                        <option key={item.Code} value={item.Code}>
                          {item.Description}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col className="external-filter-col">
                    <b>Expense Type</b>
                    <Input
                      type="select"
                      className="form-select external-filter-input"
                      value={externalFilters?.expenseType}
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          expenseType: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select Expense Type</option>
                      {expenseTypeData?.map(item => (
                        <option key={item.Code} value={item.Code}>
                          {item.Description}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col className="external-filter-col">
                    <b>SOR No.</b>
                    <Input
                      type="text"
                      className="form-control external-filter-input"
                      placeholder="Enter SOR No."
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          sorNo: e.target.value,
                        }))
                      }
                      value={externalFilters?.sorNo || ''}
                    />
                  </Col>
                </div>
                <div className="d-flex flex-row gap-2">
                  <Col className="d-flex justify-content-center">
                    <div className="external-filter-btns gap-2">
                      <Button
                        type="button"
                        className="custom-light-btn"
                        onClick={() => {
                          applyExternalFilters({
                            ...initialState,
                          });
                          setExternalFilters(prev => ({
                            ...prev,
                            ...initialState,
                          }));
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        type="button"
                        className="custom-btn"
                        onClick={() => {
                          if (!externalFiltersValidation()) {
                            return;
                          }
                          applyExternalFilters(externalFilters);
                        }}
                      >
                        Search
                      </Button>
                    </div>
                  </Col>
                </div>
              </div>
            </Col>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={claimHeaders}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive">
            <Table
              hover
              className="custom-table align-middle text-nowrap"
              id="expense-enquiry"
            >
              <thead>
                <tr>
                  {Object.entries(claimHeaders).map(([key, value]) => (
                    <th key={key}>
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => {
                            handleAutoSort(key);
                          }}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                          isLoading={isLoadingColumn}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
    </div>
  );
}

export default ExpenseEnquiry;
